import { BlockType } from "../world/block-registry";

export enum BiomeType {
  PLAINS = 0,
  FOREST = 1,
  DESERT = 2,
  MOUNTAINS = 3,
  SWAMP = 4,
  TUNDRA = 5,
}

export type BiomeConfig = {
  type: BiomeType;
  name: string;
  surfaceBlock: BlockType;
  subSurfaceBlock: BlockType;
  amplitude: number; // terrain height variation
  baseOffset: number; // offset from sea level
  treeChance: number; // 0-1 probability per column
  structureChance: number; // 0-1 probability per chunk
  fogColor?: [number, number, number];
};

export const BIOME_CONFIGS: Record<BiomeType, BiomeConfig> = {
  [BiomeType.PLAINS]: {
    type: BiomeType.PLAINS,
    name: "Plains",
    surfaceBlock: BlockType.GRASS,
    subSurfaceBlock: BlockType.DIRT,
    amplitude: 8, // gentle rolling
    baseOffset: 2,
    treeChance: 0.008, // sparse trees
    structureChance: 0.06, // villages common
  },
  [BiomeType.FOREST]: {
    type: BiomeType.FOREST,
    name: "Forest",
    surfaceBlock: BlockType.GRASS,
    subSurfaceBlock: BlockType.DIRT,
    amplitude: 12,
    baseOffset: 4,
    treeChance: 0.08, // dense trees
    structureChance: 0.02,
  },
  [BiomeType.DESERT]: {
    type: BiomeType.DESERT,
    name: "Desert",
    surfaceBlock: BlockType.SAND,
    subSurfaceBlock: BlockType.SAND,
    amplitude: 5, // flat dunes
    baseOffset: -2,
    treeChance: 0, // no trees
    structureChance: 0.03, // ruins
  },
  [BiomeType.MOUNTAINS]: {
    type: BiomeType.MOUNTAINS,
    name: "Mountains",
    surfaceBlock: BlockType.STONE,
    subSurfaceBlock: BlockType.STONE,
    amplitude: 45, // tall peaks
    baseOffset: 15,
    treeChance: 0.01,
    structureChance: 0.01,
  },
  [BiomeType.SWAMP]: {
    type: BiomeType.SWAMP,
    name: "Swamp",
    surfaceBlock: BlockType.GRASS,
    subSurfaceBlock: BlockType.DIRT,
    amplitude: 3, // very flat, near water
    baseOffset: -3,
    treeChance: 0.04,
    structureChance: 0.02,
  },
  [BiomeType.TUNDRA]: {
    type: BiomeType.TUNDRA,
    name: "Tundra",
    surfaceBlock: BlockType.STONE,
    subSurfaceBlock: BlockType.DIRT,
    amplitude: 10,
    baseOffset: 5,
    treeChance: 0.005,
    structureChance: 0.01,
  },
};

/**
 * Determine biome from temperature and humidity values.
 * Both range from -1 to 1.
 */
export function getBiome(temperature: number, humidity: number): BiomeType {
  // Hot + dry = desert
  if (temperature > 0.4 && humidity < -0.2) return BiomeType.DESERT;

  // Cold = tundra or mountains
  if (temperature < -0.4) {
    if (humidity > 0.2) return BiomeType.TUNDRA;
    return BiomeType.MOUNTAINS;
  }

  // High humidity = swamp or forest
  if (humidity > 0.3) {
    if (temperature < 0) return BiomeType.SWAMP;
    return BiomeType.FOREST;
  }

  // Moderate = mountains if very rough terrain indicator
  if (temperature < -0.1 && humidity < -0.3) return BiomeType.MOUNTAINS;

  // Default = plains
  return BiomeType.PLAINS;
}
